import { Component, Inject, OnInit, Self, inject } from '@angular/core';
import { PageTitleService } from '@app/shared/services/page-title.service';
import { Transition } from '@uirouter/angular';
import { switchMap, takeUntil } from 'rxjs/operators';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import map from 'lodash-es/map';

import browserHelper from '@utils/browser-helper';
import dayjs from 'dayjs';

import { CompanyService } from '@app/shared/services/company.service';
import { LegalTermsResource } from '@app/shared/modules/resources/legal-terms.resource';
import { NgOnDestroy } from '@app/shared/services/ng-on-destroy.service';
import { UserManager } from '@app/shared/services/user-manager.service';
import { ScrollToIdService } from '@app/shared/services/scroll-to-id.service';

@Component({
  providers: [NgOnDestroy],
  selector: 'app-legal-terms',
  templateUrl: './legal-terms.component.html',
  standalone: false
})
export class LegalTermsComponent implements OnInit {
  isLoaded = false;
  isPublic = this.APP === 'public';
  documentContents?: string;
  lastUpdate = '';

  constructor(
    @Self() private destroy: NgOnDestroy,
    @Inject('APP') public APP: string,
    private userManager: UserManager,
    private companyManager: CompanyService,
    private pageTitle: PageTitleService,
    private transition: Transition,
    private legalTermsResource: LegalTermsResource,
    private scrollToIdService: ScrollToIdService
  ) {}

  ngOnInit() {
    this.legalTermsResource
      .getLocales()
      .pipe(
        switchMap(locales => {
          const possibleLocales = compact([
            this.userManager.data.locale, // 1 User preference
            browserHelper.preferredLanguage, // 2 browser
            locales.primary // value from api
          ]);

          const preferredLocale = compact(
            map(possibleLocales, pl => find(locales.available, al => this.compareLocales(pl, al)))
          )[0];

          return this.legalTermsResource.get(preferredLocale);
        }),
        takeUntil(this.destroy)
      )
      .subscribe(terms => {
        this.lastUpdate = dayjs(terms.updatedAt).format('YYYY-MM-DD');

        const camelCasedDocument = this.document.replace(/-([a-z])/g, g => g[1].toUpperCase());
        this.documentContents = terms[camelCasedDocument];

        this.pageTitle.setTitle(`page-titles.legal-terms.${this.document}`);

        this.isLoaded = true;
      });

    if (this.isPublic) {
      this.scrollToIdService.scrollToId();
    }
  }

  get document() {
    return this.transition.params().document as string;
  }

  get companyName() {
    return this.companyManager.data.name;
  }

  compareLocales(a: string, b: string) {
    const shortLocaleA = a.substr(0, 2).toLowerCase();
    const shortLocaleB = b.substr(0, 2).toLowerCase();

    return shortLocaleA === shortLocaleB;
  }
}
